import { useGetBanks } from '@modules/banks/hooks/queries/useGetBanks';
import { IconChevronDown } from '@tabler/icons-react';
import {
  Select,
  Portal,
  Flex,
  Box,
  Text,
  InputLabel,
  InputDescription,
} from '@huspy/briks-web';
import {
  Image, CloseIcon, Skeleton, Tooltip
} from '@mantine/core';
import {
  CSSProperties, useEffect, useMemo, useState
} from 'react';
import briksTheme from '@shared/briks-theme';
import { Bank as BankIcon, Check } from '@huspy/briks-icons';
import { BankSubmissionChannelType } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { BankSubmissionStatusBadge } from '@modules/opportunities/presentation/pages/CaseCreation/components/BankSubmissionStatusBadge';
import { Bank } from '@modules/banks/api/types';

const createBankProps = (
  bank: Bank,
  submissionChannelType: BankSubmissionChannelType | null = null,
  isDisabled: boolean = false
): BankItem => ({
  value: bank.external_id,
  label: bank.title,
  icon: bank.icon!,
  submissionChannelType,
  disabled: isDisabled,
  ...bank,
});

type BankItem = {
  value: string;
  label: string;
  icon: string;
  submissionChannelType: BankSubmissionChannelType | null;
  disabled: boolean;
};

type SelectedBank = {
  id: string;
  icon: string;
  name: string;
  submissionChannelType: BankSubmissionChannelType | null;
} & Record<string, any>;

type BankMultiSelectProps = {
  label?: string;
  placeholder?: string;
  maxBanks?: number;
  defaultValue?: SelectedBank[];
  onValueChange(v: SelectedBank[]): void;
  isError?: boolean;
  error?: string;
  disabled?: boolean;
  disabledBanks?: string[],
  isOpened?: boolean;
};

const getStyles = (
  isError: boolean | undefined,
  isDisabled: boolean | undefined
): CSSProperties | undefined => {
  if (isError) {
    return {
      border: `1px solid ${briksTheme.colors['error.800']}`,
      backgroundColor: briksTheme.colors['error.100'],
    };
  }
  if (isDisabled) {
    return {
      color: briksTheme.colors['neutral.900'],
      backgroundColor: briksTheme.colors['neutral.50'],
      opacity: 0.4,
    };
  }
};

const sortBySubmissionChannelType = (arr: BankItem[]): BankItem[] => arr.sort((a, b) => {
  if (a.submissionChannelType && !b.submissionChannelType) {
    return -1;
  }
  if (!a.submissionChannelType && b.submissionChannelType) {
    return 1;
  }
  return 0;
});

const BankMultiSelectSkeleton = () => (
  <Tooltip.Floating label='Loading banks...'>
    <Skeleton
      w='calc(100% + 2px)'
      h={ briksTheme.spacing['11'] }
      pos='absolute'
      left='-1px'
      style={ { cursor: 'auto' } }
      onClick={ (e) => {
        e.stopPropagation();
      } }
    />
  </Tooltip.Floating>
);

const BankValue = ({
  value,
  label,
  icon,
  disabled,
  onClick,
}: BankItem & { onClick?: Function }) => (
  <Flex
    align='center'
    gap='1'
    bg='base.white'
    borderStyle='solid'
    borderWidth='1px'
    borderColor='border-secondary'
    borderRadius='radius-2'
    padding='1'
    maxH='34px'
    opacity={ disabled ? '0.5' : '1' }
  >
    {icon && (
      <Image
        src={ icon }
        height={ 24 }
        width={ 24 }
        style={ { borderRadius: 'full' } }
      />
    )}

    {!icon && (
      <Flex align='center' justify='center' h='24px' w='24px'>
        <BankIcon color={ briksTheme.colors['neutral.600'] } />
      </Flex>
    )}

    <Box maxWidth='70%'>
      <Text overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
        {label}
      </Text>
    </Box>

    {!disabled && (
      <Box
        w='5'
        h='5'
        marginLeft='2'
        flex='1'
        alignContent='center'
        alignItems='center'
        onClick={ (e) => {
          e.stopPropagation();
          if (onClick) onClick(value);
        } }
      >
        <CloseIcon width={ 13 } color={ briksTheme.colors['black.32%'] } />
      </Box>
    )}
  </Flex>
);

// React treats `[]` as a new array reference on every render, causing unnecessary re-renders.
// By defining `EMPTY_ARRAY` outside the component, we ensure a stable reference
// and prevent `useMemo` from recalculating unnecessarily.
const EMPTY_ARRAY: string[] = [];

export const BankMultiSelect = ({
  label,
  maxBanks,
  placeholder,
  defaultValue,
  onValueChange,
  isError,
  error,
  disabled,
  disabledBanks = EMPTY_ARRAY,
  isOpened = false,
}: BankMultiSelectProps) => {
  const { data: banks, isLoading: isBanksLoading } = useGetBanks();

  const [items, setItems] = useState<BankItem[]>([]);
  const [selectedBanks, setSelectedBanks] = useState<BankItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const memoizedDisabledBanks = useMemo(() => disabledBanks, [disabledBanks]);

  useEffect(() => {
    if (!banks) return;

    if (defaultValue) {
      const selectedBankIDs = new Set(defaultValue.map((e) => e.id));
      const allBanks = banks.map((bank) => {
        const submissionChannelType = defaultValue.find((sb) => sb.id === bank.external_id)?.submissionChannelType ?? null;
        const isDisabled = memoizedDisabledBanks.includes(bank.external_id);
        return createBankProps(bank, submissionChannelType, isDisabled);
      });
      const defaultSelectedBanks = allBanks.filter((bank) => selectedBankIDs.has(bank.value));

      setItems(allBanks);
      setSelectedBanks(defaultSelectedBanks);
    } else {
      const allBanks = banks.map((bank) => createBankProps(bank));

      setItems(allBanks);
    }
  }, [banks, defaultValue, memoizedDisabledBanks]);

  const updateSelectedBanks = (newBankSelection: BankItem[]) => {
    if (maxBanks && newBankSelection.length > maxBanks) return;
    setSelectedBanks(newBankSelection);
    onValueChange(
      newBankSelection.map((bank) => ({
        id: bank.value,
        submissionChannelType: bank.submissionChannelType,
        icon: bank.icon,
        name: bank.label,
      }))
    );
  };

  const removeSelectedBank = (bankId: string) => {
    if (disabledBanks.includes(bankId)) return;
    const newBankSelection = selectedBanks.filter(
      (bank) => bank.value !== bankId
    );
    setSelectedBanks(newBankSelection);
    onValueChange(
      newBankSelection.map((bank) => ({
        id: bank.value,
        submissionChannelType: bank.submissionChannelType,
        icon: bank.icon,
        name: bank.label,
      }))
    );
  };

  useEffect(() => {
    if (items.length > 0 && isOpened) {
      setIsOpen(true);
    }
  }, [items, isOpened]);

  return (
    <Select.Root
      multiple
      value={ selectedBanks.map((bank) => bank.value) }
      onValueChange={ (e) => updateSelectedBanks(e.items as BankItem[]) }
      open={ isOpen }
      onClick={ () => setIsOpen(!isOpen) }
      onPointerDownOutside={ () => setIsOpen(false) }
      size='md'
      items={ items }
      positioning={ { sameWidth: true } }
      disabled={ disabled }
    >
      {label && (
        <InputLabel isError={ isError }>
          <Text size='md' weight='medium'>
            {label}
          </Text>
        </InputLabel>
      )}
      <Select.Control>
        <Select.Trigger
          borderColor={ isOpened ? 'neutral.400' : '' }
          height='auto'
          minH='11'
          padding='1'
          style={ getStyles(isError, disabled) }
        >
          {isBanksLoading && <BankMultiSelectSkeleton />}
          {selectedBanks.length === 0 && placeholder && (
            <Text paddingLeft='2'>{placeholder}</Text>
          )}
          <Flex gap='1' wrap='wrap'>
            {sortBySubmissionChannelType(selectedBanks).map((selectedBank) => (
              <BankValue
                key={ selectedBank.value }
                value={ selectedBank.value }
                label={ selectedBank.label }
                icon={ selectedBank.icon }
                submissionChannelType={ selectedBank.submissionChannelType }
                disabled={ selectedBank.disabled }
                onClick={ removeSelectedBank }
              />
            ))}
          </Flex>
          <Select.Indicator>
            <IconChevronDown />
          </Select.Indicator>
        </Select.Trigger>
      </Select.Control>
      <Portal>
        <Select.Positioner>
          <Select.Content
            onClick={ (e) => e.stopPropagation() }
            h='250'
            style={ { overflow: 'scroll' } }
          >
            {items.map((item) => {
              const isSelected = selectedBanks.find(
                (bank) => bank.value === item.value
              );
              return (
                <Select.Item
                  key={ item.value }
                  item={ item }
                  cursor={ maxBanks && selectedBanks.length >= maxBanks && !isSelected
                    ? 'no-drop'
                    : 'pointer' }
                >
                  <Flex gap={ 2 }>
                    <Box borderRadius='100px' bg='base.white' w='24px' h='24px'>
                      {item.icon && (
                        <Image
                          src={ item.icon }
                          height={ 24 }
                          width={ 24 }
                          style={ { borderRadius: '100px' } }
                        />
                      )}

                      {!item.icon && (
                        <Flex align='center' justify='center' h='100%' w='100%'>
                          <BankIcon color={ briksTheme.colors['neutral.600'] } />
                        </Flex>
                      )}
                    </Box>
                    <Select.ItemText>{item.label}</Select.ItemText>
                  </Flex>
                  {isSelected && (
                    <Select.ItemIndicator>
                      {item.disabled
                        ? (<BankSubmissionStatusBadge bankSubmissionChannelType={ item.submissionChannelType } />)
                        : (<Check />)}
                    </Select.ItemIndicator>
                  )}
                </Select.Item>
              );
            })}
          </Select.Content>
        </Select.Positioner>
      </Portal>
      <Select.HiddenSelect />
      <InputDescription color='error.800'>{error}</InputDescription>
    </Select.Root>
  );
};
